import { useLang, useT } from '../lib/i18n';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ROLE_LABELS, ROLES, type ManagedUserDto, type Role, OWNER_LABEL } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

function CopyLink({ link, onDone }: { link: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const t = useT();
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="link-box">
        <code className="mono">{link}</code>
        <button
          className="btn btn-sm btn-primary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(link);
              setCopied(true);
            } catch {
              setCopied(false);
            }
          }}
        >
          {copied ? t('Copied') : t('Copy')}
        </button>
        <button className="btn btn-sm btn-ghost" onClick={onDone} aria-label={t('Close')}>{t('Close')}</button>
      </div>
      <span className="muted" style={{ fontSize: 13 }}>
        {t('Send this link to the person (WhatsApp or email). It works once. A first-access link lasts 72 hours; a reset link lasts 2 hours.')}
      </span>
    </div>
  );
}

export function Users() {
  const { me } = useAuth();
  const { lang } = useLang();
  const [users, setUsers] = useState<ManagedUserDto[]>([]);
  const [error, setError] = useState('');
  const [link, setLink] = useState<{ name: string; url: string } | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; email: string; role: Role }>({ name: '', email: '', role: 'office' });
  const [busy, setBusy] = useState(false);

  const t = useT();
  const load = useCallback(async () => {
    try {
      setUsers((await api<{ users: ManagedUserDto[] }>('/api/users')).users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not load the users.'));
    }
  }, []);
  useEffect(() => void load(), [load]);

  async function run(fn: () => Promise<void>) {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Something went wrong.'));
      await load();
    } finally {
      setBusy(false);
    }
  }

  const create = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await api<{ setupLink: string }>('/api/users', { method: 'POST', body: form });
      setLink({ name: form.name, url: r.setupLink });
      setForm({ name: '', email: '', role: 'office' });
      await load();
    });
  };

  const patch = (u: ManagedUserDto, change: Partial<Pick<ManagedUserDto, 'role' | 'active'>>) =>
    run(async () => {
      await api(`/api/users/${u.id}`, { method: 'PATCH', body: { version: u.version, ...change } });
      setConfirmId(null);
      await load();
    });

  const newLink = (u: ManagedUserDto) =>
    run(async () => {
      const r = await api<{ link: string }>(`/api/users/${u.id}/password-link`, { method: 'POST' });
      setLink({ name: u.name, url: r.link });
    });

  return (
    <div className="stack">
      <div className="page-head">
        <h1>{t('Users')}</h1>
        <span className="muted">{t('{n} active', { n: users.filter((u) => u.active).length })}</span>
      </div>

      {error && <div className="alert alert-bad" role="alert">{error}</div>}

      <form className="card section" onSubmit={create}>
        <h2>{t('Add a person')}</h2>
        <div className="form-row">
          <div className="field">
            <label htmlFor="n">{t('Name')}</label>
            <input id="n" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="e">{t('Email')}</label>
            <input id="e" className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="r">{t('Role')}</label>
            <select id="r" className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABELS[r])}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy}>{t('Add')}</button>
        </div>
        <span className="muted" style={{ fontSize: 13 }}>
          {t('Office plans the days. Direction can also manage users and the lists of tours, vans and guides.')}
        </span>
      </form>

      {link && (
        <div className="card section">
          <h2>{t('Link for {name}', { name: link.name })}</h2>
          <CopyLink link={link.url} onDone={() => setLink(null)} />
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr><th>{t('Name')}</th><th>{t('Email')}</th><th>{t('Role')}</th><th>{t('Status')}</th><th>{t('Last login')}</th><th /></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const self = u.id === me?.user.id;
              const locked = u.protected;
              return (
                <tr key={u.id} className={u.active ? '' : 'inactive'}>
                  <td><b>{u.name}</b>{self && <span className="muted"> {t('(you)')}</span>}</td>
                  <td>{u.email}</td>
                  <td>
                    {locked ? <span className="badge badge-accent">{t(OWNER_LABEL)}</span> : <select className="select" style={{ minHeight: 34, width: 'auto' }} value={u.role} disabled={busy || !u.active || locked}
                      aria-label={t('Role of {name}', { name: u.name })} onChange={(e) => patch(u, { role: e.target.value as Role })}>
                      {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABELS[r])}</option>)}
                    </select>}
                  </td>
                  <td>
                    {!u.active ? <span className="badge badge-bad">{t('Former user')}</span>
                      : u.hasPassword ? <span className="badge badge-good">{t('Active')}</span>
                      : <span className="badge badge-accent">{t('Waiting for first login')}</span>}
                  </td>
                  <td className="muted">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : t('Never')}</td>
                  <td>
                    <div className="row-actions">
                      {u.active && !locked && <button className="btn btn-sm" disabled={busy} onClick={() => newLink(u)}>{t('New password link')}</button>}
                      {u.active && !self && !locked && confirmId !== u.id && (
                        <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => setConfirmId(u.id)}>{t('Deactivate')}</button>
                      )}
                      {confirmId === u.id && (
                        <>
                          <span className="muted" style={{ alignSelf: 'center', fontSize: 13 }}>{t('Cut access now?')}</span>
                          <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => patch(u, { active: false })}>{t('Yes, deactivate')}</button>
                          <button className="btn btn-sm" onClick={() => setConfirmId(null)}>{t('Cancel')}</button>
                        </>
                      )}
                      {!u.active && <button className="btn btn-sm" disabled={busy} onClick={() => patch(u, { active: true })}>{t('Reactivate')}</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <span className="muted" style={{ fontSize: 13 }}>
        {t('Deactivated people can no longer log in, and their names stay in the history. Nobody is ever deleted.')}
      </span>
    </div>
  );
}
